/**
 * Convert resume sections object to ATS-friendly plain text with headers
 * CRITICAL: Includes section headers like "Professional Summary", "Skills", etc.
 * so that ATS scanners can properly detect sections
 */
export const formatResumeForATS = (sections) => {
  if (!sections) return '';
  
  // If it's a string (already formatted or JSON), try to parse it
  if (typeof sections === 'string') {
    try {
      sections = JSON.parse(sections);
    } catch (e) {
      // If parsing fails, assume it's already formatted text
      return sections;
    }
  }
  
  const parts = [];
  
  // Contact Information
  if (sections.contact) {
    if (sections.contact.name) parts.push(sections.contact.name);
    if (sections.contact.email) parts.push(sections.contact.email);
    if (sections.contact.phone) parts.push(sections.contact.phone);
    if (sections.contact.location) parts.push(sections.contact.location);
    if (sections.contact.linkedin) parts.push(sections.contact.linkedin);
    if (sections.contact.website) parts.push(sections.contact.website);
  }
  
  // Professional Summary (WITH HEADER - Critical for ATS detection)
  if (sections.summary) {
    parts.push('\nProfessional Summary');
    parts.push(sections.summary);
  }
  
  // Objective (alternative to summary)
  if (sections.objective && !sections.summary) {
    parts.push('\nObjective');
    parts.push(sections.objective);
  }
  
  // Experience (WITH HEADER)
  if (sections.experience && sections.experience.length > 0) {
    parts.push('\nExperience');
    sections.experience.forEach(exp => {
      parts.push(`\n${exp.title || exp.position || 'Position'} at ${exp.company || 'Company'}`);
      if (exp.location) parts.push(exp.location);
      if (exp.date || exp.dates) parts.push(exp.date || exp.dates);
      if (exp.description) {
        parts.push(exp.description);
      } else if (exp.bullets && Array.isArray(exp.bullets)) {
        exp.bullets.forEach(bullet => parts.push(`• ${bullet}`));
      } else if (exp.responsibilities && Array.isArray(exp.responsibilities)) {
        exp.responsibilities.forEach(resp => parts.push(`• ${resp}`));
      }
    });
  }
  
  // Education (WITH HEADER)
  if (sections.education && sections.education.length > 0) {
    parts.push('\nEducation');
    sections.education.forEach(edu => {
      const degreeLine = `${edu.degree || 'Degree'} - ${edu.school || edu.institution || 'School'}`;
      parts.push(degreeLine);
      if (edu.location) parts.push(edu.location);
      if (edu.date || edu.dates) parts.push(edu.date || edu.dates);
      if (edu.gpa) parts.push(`GPA: ${edu.gpa}`);
      if (edu.description) parts.push(edu.description);
    });
  }
  
  // Skills (WITH HEADER)
  if (sections.skills) {
    parts.push('\nSkills');
    if (Array.isArray(sections.skills)) {
      // If it's an array of strings, join with commas
      if (sections.skills.every(skill => typeof skill === 'string')) {
        parts.push(sections.skills.join(', '));
      }
      // If it's an array of objects with categories
      else {
        sections.skills.forEach(skillGroup => {
          if (skillGroup.category && skillGroup.items) {
            parts.push(`${skillGroup.category}: ${skillGroup.items.join(', ')}`);
          } else if (skillGroup.name) {
            parts.push(skillGroup.name);
          }
        });
      }
    } else if (typeof sections.skills === 'object') {
      // If it's an object with categories as keys
      Object.entries(sections.skills).forEach(([category, skillList]) => {
        if (Array.isArray(skillList)) {
          parts.push(`${category}: ${skillList.join(', ')}`);
        }
      });
    } else if (typeof sections.skills === 'string') {
      parts.push(sections.skills);
    }
  }
  
  // Certifications (WITH HEADER)
  if (sections.certifications && sections.certifications.length > 0) {
    parts.push('\nCertifications');
    sections.certifications.forEach(cert => {
      if (typeof cert === 'string') {
        parts.push(cert);
      } else if (cert.name) {
        const certLine = cert.issuer ? `${cert.name} - ${cert.issuer}` : cert.name;
        parts.push(certLine);
        if (cert.date) parts.push(`Issued: ${cert.date}`);
      }
    });
  }
  
  // Projects (WITH HEADER)
  if (sections.projects && sections.projects.length > 0) {
    parts.push('\nProjects');
    sections.projects.forEach(project => {
      parts.push(`\n${project.name || project.title || 'Project'}`);
      if (project.date) parts.push(project.date);
      if (project.description) parts.push(project.description);
      if (project.technologies) {
        parts.push(`Technologies: ${Array.isArray(project.technologies) ? project.technologies.join(', ') : project.technologies}`);
      }
    });
  }
  
  // Achievements/Awards (WITH HEADER)
  if (sections.achievements && sections.achievements.length > 0) {
    parts.push('\nAchievements');
    sections.achievements.forEach(achievement => {
      if (typeof achievement === 'string') {
        parts.push(achievement);
      } else if (achievement.title || achievement.name) {
        parts.push(achievement.title || achievement.name);
        if (achievement.description) parts.push(achievement.description);
      }
    });
  }
  
  // Languages (WITH HEADER)
  if (sections.languages && sections.languages.length > 0) {
    parts.push('\nLanguages');
    sections.languages.forEach(lang => {
      if (typeof lang === 'string') {
        parts.push(lang);
      } else if (lang.name) {
        const langLine = lang.proficiency ? `${lang.name} - ${lang.proficiency}` : lang.name;
        parts.push(langLine);
      }
    });
  }
  
  // Volunteer Experience (WITH HEADER)
  if (sections.volunteer && sections.volunteer.length > 0) {
    parts.push('\nVolunteer Experience');
    sections.volunteer.forEach(vol => {
      parts.push(`\n${vol.role || 'Volunteer'} at ${vol.organization || 'Organization'}`);
      if (vol.date) parts.push(vol.date);
      if (vol.description) parts.push(vol.description);
    });
  }
  
  return parts.filter(p => p).join('\n').trim();
};

export default formatResumeForATS;
